import { Injectable } from "@angular/core";
import { ActivatedRouteSnapshot, CanActivate, Router, RouterStateSnapshot } from "@angular/router";
import { ClerkService } from "ngx-clerk-iliad";
import { catchError, from, Observable, of, switchMap, take } from "rxjs";
import { UserService } from "../../services/user.service";
import { reportToSentry } from "../sentry/reportFunctions";

@Injectable({ providedIn: "root" })
export class MfaRequirementGuard implements CanActivate {
  constructor(
    private readonly clerk: ClerkService,
    private readonly router: Router,
    private readonly userService: UserService,
  ) {}

  canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<boolean> {
    // Include the full URL so that if we're on a satellite, we'll redirect after sign-in correctly
    const signInFallbackRedirectUrl = `${window.location.origin}${state.url}`;

    return this.clerk.user$.pipe(
      take(1),
      switchMap((clerkUser) => {
        // If user is not signed in
        if (!clerkUser?.id) {
          this.clerk.redirectToSignIn({ signInFallbackRedirectUrl });
          return of(false);
        }

        return this.userService.getSelf$().pipe(
          take(1),
          switchMap((connectUser) => {
            // We should have the user here, but if not, redirect to sign-in
            if (!connectUser) {
              this.clerk.redirectToSignIn({ signInFallbackRedirectUrl });
              return of(false);
            }
            if (connectUser.isInternal || clerkUser.twoFactorEnabled) {
              return of(true);
            }
            // Snapshot says no MFA — but `user$` can be stale (e.g. localStorage rehydration
            // on second sign-in), so reload from Clerk before blocking the user.
            return from(clerkUser.reload()).pipe(
              catchError((error: unknown) => {
                // Network/session failure: send the user to the safe landing page rather
                // than acting on the possibly-stale snapshot.
                if (error instanceof Error) {
                  reportToSentry(error, "Auth.MfaCheck");
                } else {
                  reportToSentry(new Error("Clerk user reload failed during MFA check"), "Auth.MfaCheck", {
                    extra: { originalError: error },
                  });
                }
                this.router.navigate(["/organizations/select"]);
                return of(null);
              }),
              switchMap((freshClerkUser) => {
                if (freshClerkUser === null) {
                  return of(false);
                }
                if (!freshClerkUser.twoFactorEnabled) {
                  this.router.navigate(["/organizations/select"]);
                  this.userService.openClerkUserProfile(connectUser, {
                    // This class makes it sure that the users' focus is on the MFA section of the profile
                    modalContentCssClasses: "cl-connect-mfa-required-dialog",
                    overrides: {
                      // Start the user at the security section of the profile, where they can setup MFA
                      __experimental_startPath: "/security",
                    },
                  });
                  return of(false);
                }
                return of(true);
              }),
            );
          }),
        );
      }),
    );
  }
}
